import { expireSession, sessionEpoch } from "./session-expiry.js";
import {
  clientInstanceRequestHeaders,
  ensureClientInstanceToken,
} from "./session-lifecycle.js";
import { authenticationScopeHeaders, csrfCookieName } from "./app-mode.js";

export interface CurrentUser {
  id: string;
  username: string;
  displayName: string;
  permissions: string[];
}

export interface CurrentUserSession extends CurrentUser {
  mustChangePassword: boolean;
}

export interface LoginResult {
  user: CurrentUser;
  mustChangePassword: boolean;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly correlationId: string | undefined;

  constructor(
    message: string,
    status: number,
    code?: string,
    correlationId?: string,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.correlationId = correlationId;
  }
}

export interface ServiceHealth {
  status: "ok" | "degraded";
  readinessContract: 1;
  sourceVersion: string;
  database: "up" | "down" | "not-checked";
  schema: "current" | "pending" | "invalid" | "not-checked";
  schemaVersion: number | null;
  requiredSchemaVersion: number | null;
  pendingMigrations: number[];
  message?: string;
}

export const runtimeSourceVersion = (): string => {
  const value: unknown = (import.meta.env as Readonly<Record<string, unknown>>)[
    "VITE_CEM_SOURCE_VERSION"
  ];
  return typeof value === "string" && value.trim() !== ""
    ? value.trim()
    : "versione non identificata";
};

export const validateReadinessPayload = (
  payload: unknown,
  expectedSourceVersion = runtimeSourceVersion(),
): ServiceHealth => {
  if (typeof payload !== "object" || payload === null)
    throw new Error("Il servizio dati ha restituito una risposta non valida.");
  const health = payload as Partial<ServiceHealth>;
  if (
    (health.status !== "ok" && health.status !== "degraded") ||
    health.readinessContract !== 1 ||
    typeof health.sourceVersion !== "string" ||
    typeof health.schema !== "string"
  )
    throw new Error(
      "Frontend e servizio dati appartengono a versioni diverse. Chiudere questa scheda e riavviare Gestionale CEM.exe.",
    );
  if (
    expectedSourceVersion !== "versione non identificata" &&
    health.sourceVersion !== expectedSourceVersion
  )
    throw new Error(
      `Versione frontend ${expectedSourceVersion} diversa dal servizio dati ${health.sourceVersion}. Chiudere questa scheda e riavviare Gestionale CEM.exe.`,
    );
  return health as ServiceHealth;
};

const cookie = (name: string): string | undefined =>
  document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`))
    ?.slice(name.length + 1);

const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const requestEpoch = sessionEpoch();
  const csrf = cookie(csrfCookieName);
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers: {
      accept: "application/json",
      ...(init?.body === undefined
        ? {}
        : { "content-type": "application/json" }),
      ...(csrf === undefined
        ? {}
        : { "x-csrf-token": decodeURIComponent(csrf) }),
      ...init?.headers,
      ...clientInstanceRequestHeaders(),
      ...authenticationScopeHeaders,
    },
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as {
      error?: { code?: string; message?: string; correlationId?: string };
    };
    expireSession(requestEpoch, response.status, payload.error?.code);
    throw new ApiRequestError(
      payload.error?.message ?? "Errore HTTP " + String(response.status),
      response.status,
      payload.error?.code,
      payload.error?.correlationId,
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
};

const readiness = async (): Promise<ServiceHealth> => {
  const response = await fetch("/health/ready", {
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  return validateReadinessPayload(await response.json());
};

export const api = {
  readiness,
  currentUser: () => request<CurrentUserSession>("/api/v1/auth/me"),
  login: (username: string, password: string) => {
    ensureClientInstanceToken();
    return request<LoginResult>("/api/v1/auth/session", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
  },
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>("/api/v1/auth/password", {
      method: "PUT",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  logout: () => request<void>("/api/v1/auth/session", { method: "DELETE" }),
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: "DELETE",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
};
export async function fetchApiBlob(path: string): Promise<Blob> {
  const requestEpoch = sessionEpoch();
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: {
      ...clientInstanceRequestHeaders(),
      ...authenticationScopeHeaders,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: { message?: string; code?: string };
    };
    expireSession(requestEpoch, response.status, body.error?.code);
    throw new Error(body.error?.message ?? "Download non riuscito.");
  }
  return response.blob();
}
export async function downloadApiFile(
  path: string,
  fileName: string,
): Promise<void> {
  const content = await fetchApiBlob(path),
    url = URL.createObjectURL(content),
    link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
} // Keep the same operation ID after an uncertain response; do not queue offline work.
const attendanceOperations = new Map<string, string>();
export async function attendancePost<T>(
  path: string,
  body: Record<string, unknown>,
): Promise<T> {
  const payload = { ...body };
  delete payload.operationId;
  const key = JSON.stringify([path, payload, clientInstanceRequestHeaders()]);
  const id = attendanceOperations.get(key) ?? crypto.randomUUID();
  attendanceOperations.set(key, id);
  try {
    const result = await api.post<T>(path, { ...payload, operationId: id });
    attendanceOperations.delete(key);
    return result;
  } catch (error) {
    if (error instanceof ApiRequestError && error.status < 500)
      attendanceOperations.delete(key);
    throw error;
  }
}
